import { onAuthStateChanged } from 'firebase/auth';
import { auth, callable, db as tapDb, contentDb } from '$lib/firebase/firebase';
import {
    handleAuthTokenHandoff,
    handleImpersonationHandoff,
    createCrossAppSignOut,
    verifySessionAppClaim,
    getUserApps as sharedGetUserApps,
    switchToApp as sharedSwitchToApp,
    resolveAppUrl,
    buildAppSwitchTargets,
} from '@repo/firebase';
import { replaceState } from '$app/navigation';
import { page } from '$app/state';
import { get } from 'svelte/store';
import { showLoader } from '@repo/stores/Actions';
import { translationActive, translationLanguage } from '@repo/stores/Language';
import { readTranslationSettings } from '@repo/translation';
import { ghostToken } from '@repo/stores/Ghost.js';
import { postHogCapture, posthog } from '@repo/config/postHogHelper';
import { TARGET_APPS } from '@repo/config/Global.js';
import { getTapUser, getUserSubscriptions } from './DB.js';
import {
    canAccessResource,
    hasAction,
    hasContentAction,
    hasRole,
    markRolesReady,
    rebuildSubscriptions,
    resetAccess,
    rolesReady,
} from './access.js';
import { currentUser, tapUser as tapUserStore } from './stores.js';

export {
    currentUser,
    canAccessResource,
    hasAction,
    hasContentAction,
    hasRole,
    rolesReady,
};

// SvelteKit's initial navigate() re-syncs history at the end of the first
// render with the URL it captured at nav-start — which still had `?token=`.
// Use $app/navigation's replaceState (the router-aware variant) and defer it
// to the next macrotask so _start_router() has settled before we run.
function scrubTokenUrl(cleanUrl) {
    setTimeout(() => {
        try {
            replaceState(cleanUrl, page.state);
        } catch {
            // Router still not ready — nothing more we can do cleanly here.
        }
    }, 0);
}

// Consume ?token=... from URL (cross-app handoff from Auth app) at module load.
let authTokenSignInPromise = handleAuthTokenHandoff(auth, {
    onError: () => {
        showLoader.set(false);
        redirectToAuth();
    },
    scrubUrl: scrubTokenUrl,
});

// Consume ?impersonate=... from URL (cross-app handoff from Admin app) at
// module load. The admin app stored the impersonation custom token server-side
// keyed by this nonce; we redeem it and sign in as the impersonated user.
let impersonationSignInPromise = handleImpersonationHandoff(auth, callable, {
    onError: () => {
        ghostToken.set(null);
        showLoader.set(false);
        redirectToAuth();
    },
    scrubUrl: scrubTokenUrl,
    onActive: () => ghostToken.set('active'),
});

onAuthStateChanged(auth, async (user) => {
    // Wait for custom token sign-in to complete if it's in progress.
    // Fall through with auth.currentUser rather than returning — Firebase may
    // not re-fire onAuthStateChanged when the UID didn't change (same user
    // already cached in IndexedDB on this origin).
    if (authTokenSignInPromise) {
        const pending = authTokenSignInPromise;
        authTokenSignInPromise = null;
        try {
            await pending;
        } catch {
            // handled via handleAuthTokenHandoff's onError
        }
        user = auth.currentUser;
        if (!user) return;
    }

    // Same pattern as authTokenSignInPromise above — impersonation sign-in
    // must complete before the app gates evaluate the session.
    if (impersonationSignInPromise) {
        const pending = impersonationSignInPromise;
        impersonationSignInPromise = null;
        try {
            await pending;
        } catch {
            // handled via handleImpersonationHandoff's onError
        }
        user = auth.currentUser;
        if (!user) return;
    }

    if (user?.uid) {
        // Gate: the session's ID token must carry targetApp === 'tap', otherwise
        // Firestore rules will reject everything. Kick stale/cross-app sessions
        // back to the Auth app to re-pick.
        const { ok, claim } = await verifySessionAppClaim(
            user,
            TARGET_APPS.TAP
        );
        if (!ok) {
            console.warn(
                `[tap-auth] session claim mismatch: expected tap, got ${claim ?? 'none'}`
            );
            await auth.signOut();
            resetAccess();
            currentUser.set(null);
            tapUserStore.set(null);
            showLoader.set(false);
            redirectToAuth();
            return;
        }

        try {
            await hydrateUser(user);
        } catch (err) {
            console.error('[tap-auth] hydration failed', err);
        }
        currentUser.set(user);
        showLoader.set(false);
    } else {
        resetAccess();
        currentUser.set(null);
        tapUserStore.set(null);
        showLoader.set(false);
        redirectToAuth();
    }
});

/**
 * Pull the TAP user doc and subscriptions from tap-db, then rebuild the
 * content-action store so `hasContentAction` works.
 *
 * TAP users have no role docs — we skip the role hydration path entirely.
 * All their access comes from their subscription's contentActions.
 */
async function hydrateUser(user) {
    await refreshTapAccess(user.uid);

    const tapUserDoc = get(tapUserStore);
    if (tapUserDoc) {
        // Shared components (the translation settings modal) read per-user
        // settings off `systemData`, mirroring Education's user shape.
        user.systemData = tapUserDoc;

        // Stash redeemedCodes on the user object so UI can show "N books
        // redeemed" without re-reading tap-db.
        if (tapUserDoc.redeemedCodes) {
            user.redeemedCodes = tapUserDoc.redeemedCodes;
        }
    }

    // TAP users have no role docs — flip the access gate to ready here so
    // downstream role/content-action checks can unblock.
    markRolesReady();
}

/**
 * (Re)load the tap-db user doc + subscriptions and rebuild the content-action
 * store. Also called after an in-app code redemption (with `noCache`) so the
 * shelves, access stats and codes table update without a reload.
 */
export async function refreshTapAccess(uid, { noCache = false } = {}) {
    const [tapUserDoc, subscriptions] = await Promise.all([
        getTapUser(uid).catch((err) => {
            console.warn('[tap-auth] getTapUser failed', err);
            return null;
        }),
        getUserSubscriptions({ uid, noCache }).catch((err) => {
            console.warn('[tap-auth] getUserSubscriptions failed', err);
            return null;
        }),
    ]);

    // User-doc hydration runs before the subscription guard below: a failed
    // subscription fetch should not also cost the user their saved settings.
    tapUserStore.set(tapUserDoc);

    // Only seed the translation stores when the doc actually came back —
    // treating a fetch failure as an empty doc would reset every setting to
    // its default on each load.
    if (tapUserDoc) {
        const { language, active } = readTranslationSettings(tapUserDoc);
        if (get(translationLanguage) !== language) {
            translationLanguage.set(language);
        }
        if (get(translationActive) !== active) {
            translationActive.set(active);
        }
    }

    if (!subscriptions) return;

    await rebuildSubscriptions(subscriptions);
}

function redirectToAuth() {
    if (typeof window === 'undefined') return;
    const authUrl = import.meta.env.VITE_AUTH_URL;
    if (!authUrl) return;
    try {
        const target = new URL(authUrl);
        target.searchParams.set('returnTo', window.location.href);
        window.location.href = target.toString();
    } catch (err) {
        console.error('[tap-auth] failed to build auth redirect', err);
        window.location.href = authUrl;
    }
}

// Returns the reactive store — matches Education's contract. The DocumentViewer
// subscribes to it at mount (`getCurrentUser().subscribe(u => …)`).
function getCurrentUser() {
    return currentUser;
}

async function getIdToken() {
    if (!auth.currentUser) return null;
    return auth.currentUser.getIdToken();
}

const signOut = createCrossAppSignOut({
    auth,
    callable,
    authUrl: import.meta.env.VITE_AUTH_URL,
    databases: [tapDb, contentDb],
    onBeforeRedirect: () => {
        postHogCapture('sign_out');
        resetAccess();
        currentUser.set(null);
        tapUserStore.set(null);
        posthog.reset();
    },
});

const APP_URLS = {
    [TARGET_APPS.EDUCATION]: () => import.meta.env.VITE_EDUCATION_URL,
    [TARGET_APPS.TAP]: () => import.meta.env.VITE_TAP_URL,
    [TARGET_APPS.ADMIN]: () => import.meta.env.VITE_ADMIN_URL,
    [TARGET_APPS.PERSONAL]: () => import.meta.env.VITE_PERSONAL_URL,
};

async function getUserApps() {
    return sharedGetUserApps(auth.currentUser, callable);
}

async function getAppSwitchTargets() {
    return buildAppSwitchTargets(await getUserApps(), {
        appUrls: APP_URLS,
        currentApp: TARGET_APPS.TAP,
    });
}

async function switchToApp(targetApp) {
    return sharedSwitchToApp({
        callable,
        targetApp,
        appUrl: resolveAppUrl(APP_URLS, targetApp),
    });
}

export {
    getCurrentUser,
    getIdToken,
    signOut,
    getUserApps,
    getAppSwitchTargets,
    switchToApp,
};
